/**
 * Experiment slot registry — the single source of truth for every
 * in-product A/B test the agents (and humans) are allowed to run.
 *
 * Why a closed registry instead of free-form flag keys?
 *
 *   - The Marketing/Growth agents can't fabricate new tests on a whim.
 *     They can only propose new variant copy for slots that already
 *     exist in code (because someone shipped the React/HTML scaffolding
 *     to render variants). This keeps "agent suggested" experiments
 *     from piling up as drafts that have no path to production.
 *
 *   - Winner-detection cron has a deterministic list of slots to check.
 *     No mismatch between "what's running in the app" and "what we're
 *     measuring".
 *
 *   - Type-safe variant keys end-to-end: `useExperimentVariant(slot)`
 *     returns one of the slot's literal variant ids, so the React
 *     component branches stay narrow.
 *
 * Each slot binds to a PostHog feature flag with the same `key`. The
 * variant assignment uses PostHog's multivariate flags (so allocation
 * lives in the PostHog dashboard, not in code). This file just declares
 * the slot's existence and what its variants ARE — not what % each gets.
 *
 * To add a new slot:
 *   1. Add an entry to `EXPERIMENT_SLOTS` below.
 *   2. Create a matching multivariate feature flag in PostHog with the
 *      same `key` and the same variant `id`s.
 *   3. Use `useExperimentVariant(slot)` from React to render the
 *      assigned variant; fire `track('EXPERIMENT_VIEWED', ...)` once
 *      per session per slot (the hook does this for you).
 *   4. At the conversion call-site, fire `track('EXPERIMENT_CONVERTED',
 *      { slot, variant })` so winner detection has a numerator.
 *
 * To kill a slot: change its `status` to `'archived'`. The cron stops
 * computing winners, the agent stops proposing copy variations, but
 * historical events stay queryable.
 */

export type ExperimentStatus = 'active' | 'archived';

/**
 * One variant of an experiment slot. `id` MUST match the variant key
 * configured in PostHog for the slot's feature flag.
 *
 * `isControl` flags the baseline against which winners are compared.
 * Exactly one variant per slot must be control.
 */
export interface ExperimentVariant {
  id: string;
  /// Human-readable label for the admin UI / agent reasoning.
  label: string;
  /// True for the baseline / current-production variant.
  isControl?: boolean;
  /// Optional copy payload for the agent's `draft_ab_test` proposals.
  /// React components that render the slot read variant content from
  /// their own constants (the agent can't reach into your code), so this
  /// is purely informational — the agent sees the existing variant copy
  /// when it proposes alternatives.
  copy?: Record<string, string>;
}

/**
 * Per-slot copy length budget. The agent's `draft_ab_test` tool enforces
 * these so a proposed variant can't break the React layout (a 3x-longer
 * headline pushing pricing cards off the screen, etc).
 *
 *   - `lengthToleranceRatio`: max relative length delta vs the control's
 *     same field. e.g. `0.4` → variant must be within ±40% of control's
 *     character count. Default `DEFAULT_COPY_LENGTH_TOLERANCE` (40%).
 *   - `maxCharsByField`: absolute hard cap per field. Wins over the
 *     tolerance ratio when stricter (so a 200-char "control" can't license
 *     a 280-char variant if `headline: 60` is set).
 */
export interface CopyConstraints {
  lengthToleranceRatio?: number;
  maxCharsByField?: Record<string, number>;
}

export const DEFAULT_COPY_LENGTH_TOLERANCE = 0.4;

export interface ExperimentSlot {
  /// Unique slot key. Also the PostHog feature flag key. Use kebab-case.
  key: string;
  /// One-line description of what this slot is testing.
  description: string;
  /// Where in the product the slot renders. Drives admin UI grouping.
  surface: 'billing-modal' | 'onboarding' | 'paywall' | 'editor' | 'landing' | 'email';
  /// Primary metric event name. Conversion rate = `EXPERIMENT_VIEWED`s
  /// for this slot followed by this event for the same user (within
  /// `conversionWindowDays`).
  primaryMetricEvent: string;
  /// Days after EXPERIMENT_VIEWED within which a primary-metric event
  /// counts as a conversion. Default 7 — long enough for delayed
  /// signups, short enough to not eat the next experiment.
  conversionWindowDays: number;
  /// Variants. First entry is rendered when PostHog flag eval fails or
  /// returns an unknown variant id (graceful degradation to control).
  /// Marked `readonly` so `EXPERIMENT_SLOTS as const` can satisfy this
  /// interface without a deep-clone.
  variants: readonly [ExperimentVariant, ...ExperimentVariant[]];
  /// Copy length budget. Optional — falls back to
  /// `{ lengthToleranceRatio: DEFAULT_COPY_LENGTH_TOLERANCE }` when omitted.
  copyConstraints?: CopyConstraints;
  /// `active` slots are evaluated by the winner-detection cron and the
  /// agent can propose new variant copy. `archived` slots are read-only
  /// historical reference.
  status: ExperimentStatus;
}

/**
 * Live experiment slots. Edit here to add/remove a slot.
 *
 * Convention: control variant id is `control`. Treatment variant ids
 * are `variant_a`, `variant_b`, etc. so PostHog dashboards line up.
 */
export const EXPERIMENT_SLOTS: readonly ExperimentSlot[] = [
  {
    key: 'billing-modal-headline',
    // ARCHIVED 2026-07: the React binding in PlanSelector was removed and
    // the headline is hard-coded to the control copy. Agent-drafted DB
    // variants kept shipping off-brand copy to the billing page. Do not
    // re-activate without re-wiring `useExperimentVariant` in
    // `PlanSelector/index.tsx` AND the EXPERIMENT_CONVERTED call-site in
    // `BillingClient.tsx`.
    description:
      'ARCHIVED. Step-1 headline on the PlanSelector card (settings/billing and the in-product upgrade modal). UI binding removed; headline is static.',
    surface: 'billing-modal',
    primaryMetricEvent: 'SUBSCRIPTION_STARTED',
    conversionWindowDays: 7,
    variants: [
      {
        id: 'control',
        isControl: true,
        label: 'Control: "Upgrade to unlock more features"',
        copy: {
          headline: 'Upgrade to unlock more features',
          subhead:
            'Choose the plan that works best for you and your team. Upgrade or downgrade at any time.',
        },
      },
      {
        id: 'variant_a',
        label: 'Outcome: "Build forms users actually finish"',
        copy: {
          headline: 'Build forms users actually finish',
          subhead:
            'Unlock the higher-converting form features your audience already expects.',
        },
      },
    ],
    // Headline renders inside `max-w-160` (~40rem). A 1-line layout breaks
    // around 50 chars; subhead has more room (~120 chars before the pricing
    // cards push down). Tolerance is tight on headline, looser on subhead.
    copyConstraints: {
      lengthToleranceRatio: 0.3,
      maxCharsByField: {
        headline: 50,
        subhead: 130,
      },
    },
    status: 'archived',
  },
  // ─── Slots scaffolded for the agent BUT not yet wired to React ────────────
  //
  // These two slots exist in the registry so the agent + admin Experiments
  // tab can reason about them, but the React components that should call
  // `useExperimentVariant(slot)` haven't been added yet. Until they are,
  // EXPERIMENT_VIEWED won't fire and the Experiments tab will show 0 views.
  //
  // To activate `onboarding-step-cta`:
  //   - Locate the first onboarding step's CTA button
  //     (`apps/plura/src/app/(main)/(auth)/onboarding/...`).
  //   - Replace the hard-coded button label with
  //     `useExperimentVariant(slot).variant.copy.cta`.
  //   - Fire `track('EXPERIMENT_CONVERTED', { slot, variant })` in the
  //     `FORM_CREATED` call-site for users who came from onboarding
  //     (read variant from `getStoredExperimentVariant('onboarding-step-cta')`).
  //
  // To activate `paywall-headline`:
  //   - Build a real paywall component (the current `DiamondUpgradeIcon`
  //     opens a modal directly without a paywall headline of its own).
  //   - Render `useExperimentVariant(slot).variant.copy.headline` as the
  //     modal's H1.
  //   - Fire EXPERIMENT_CONVERTED on SUBSCRIPTION_STARTED.
  {
    key: 'onboarding-step-cta',
    description:
      'CTA button label on the first onboarding step (after signup, before form creation). Scaffold only — React not wired.',
    surface: 'onboarding',
    primaryMetricEvent: 'FORM_CREATED',
    conversionWindowDays: 3,
    variants: [
      {
        id: 'control',
        isControl: true,
        label: 'Control: "Get started"',
        copy: { cta: 'Get started' },
      },
      {
        id: 'variant_a',
        label: 'Action: "Build my first form"',
        copy: { cta: 'Build my first form' },
      },
    ],
    status: 'active',
  },
  {
    key: 'paywall-headline',
    description:
      'Headline on the in-editor paywall that appears when a free user tries to use a Pro-gated block. Scaffold only — React not wired.',
    surface: 'paywall',
    primaryMetricEvent: 'SUBSCRIPTION_STARTED',
    conversionWindowDays: 7,
    variants: [
      {
        id: 'control',
        isControl: true,
        label: 'Control: "This feature is on Pro"',
        copy: { headline: 'This feature is on Pro' },
      },
      {
        id: 'variant_a',
        label: 'Direct: "Unlock with Pro"',
        copy: { headline: 'Unlock with Pro' },
      },
    ],
    status: 'active',
  },
  {
    key: 'landing-hero-headline',
    description:
      'Eyebrow + headline + subhead + CTA on the home page hero (apps/landing-app/.../HomeHeroWithAnimationForm). Highest-traffic slot — winners declare in days, not weeks.',
    surface: 'landing',
    // CTA click is the cleanest landing conversion signal — the actual
    // SIGN_UP fires on the cross-domain app.formsuite.co/sign-up page, so
    // attribution back to the variant gets messy. CTA click is one tab
    // earlier in the funnel but cleanly attributable in a single domain.
    primaryMetricEvent: 'LANDING_CTA_CLICKED',
    conversionWindowDays: 1,
    variants: [
      {
        id: 'control',
        isControl: true,
        label: 'Control: "Build smarter forms…"',
        copy: {
          eyebrow: 'The AI form builder for growth',
          headline: 'Build smarter forms and workflows. No code needed.',
          subhead:
            'Smart, customizable forms that connect with your tools and turn responses into insights.',
          cta: 'Create a Free Form',
        },
      },
    ],
    // Hero copy lives inside specific font sizes and a 960px max-width
    // card. A 1.5x-longer headline pushes the demo animation off-screen
    // on smaller viewports; an oversized eyebrow wraps and looks broken.
    // Tight tolerances + per-field caps so the agent stays inside the
    // existing visual budget.
    copyConstraints: {
      lengthToleranceRatio: 0.4,
      maxCharsByField: {
        eyebrow: 40,
        headline: 75,
        subhead: 120,
        cta: 26,
      },
    },
    status: 'active',
  },
] as const;

export type ExperimentSlotKey = (typeof EXPERIMENT_SLOTS)[number]['key'];

export function getSlot(key: string): ExperimentSlot | undefined {
  return EXPERIMENT_SLOTS.find((s) => s.key === key);
}

export function getControlVariant(slot: ExperimentSlot): ExperimentVariant {
  return slot.variants.find((v) => v.isControl) ?? slot.variants[0];
}

/**
 * Active slots only. Used by the winner-detection cron and the agent's
 * `draft_ab_test` tool so neither bothers with archived slots.
 */
export function activeSlots(): ExperimentSlot[] {
  return EXPERIMENT_SLOTS.filter((s) => s.status === 'active');
}

/**
 * Per-field length budget for a slot, derived from the control variant's
 * actual character counts plus the slot's `copyConstraints` overrides.
 *
 * Used by `draft_ab_test` to reject variants whose copy length would
 * break the React layout. Returned as `Record<fieldName, { min, max }>`.
 *
 *   - `min` = floor(controlLen * (1 - tolerance)) — typed `0` when
 *     control is empty so the validator doesn't reject empty strings on
 *     a slot whose control field is also empty.
 *   - `max` = the smaller of `ceil(controlLen * (1 + tolerance))` and
 *     the per-field absolute cap from `maxCharsByField` (when set).
 *
 * Fields the control doesn't have are NOT in the result — the variant
 * is required to use the same key set as control elsewhere in the
 * validator, so absent fields are caught separately.
 */
export function getSlotCopyLimits(slot: ExperimentSlot): Record<string, { min: number; max: number }> {
  const control = getControlVariant(slot);
  const controlCopy = control.copy ?? {};
  const tolerance = slot.copyConstraints?.lengthToleranceRatio ?? DEFAULT_COPY_LENGTH_TOLERANCE;
  const absoluteCaps = slot.copyConstraints?.maxCharsByField ?? {};

  const out: Record<string, { min: number; max: number }> = {};
  for (const [field, value] of Object.entries(controlCopy)) {
    const len = value.length;
    const ratioMax = Math.ceil(len * (1 + tolerance));
    const cap = absoluteCaps[field];
    const max = cap !== undefined ? Math.min(ratioMax, cap) : ratioMax;
    const min = Math.max(0, Math.floor(len * (1 - tolerance)));
    out[field] = { min, max };
  }
  return out;
}
